import { useAnalytics } from 'use-analytics';

import { EVENTS } from '../constants/events';
import { trackStoreEvent } from '../store-events/store-events';
import type { Product } from '../types';
import { resolveCardCtaVariant } from '@/modules/orders/views/payment-ways/card-cta';

export function useTracking() {
  const analytics = useAnalytics();

  return {
    page: () => analytics?.page?.(),

    // PRAT-19: além do Pixel/GTM, cada etapa do funil vai para a API da
    // pratiGO (Desempenho da Loja no gestor) por trackStoreEvent.
    storeView: () => {
      trackStoreEvent('store_view');
    },

    categoryView: (category: string) => {
      analytics?.track?.(EVENTS.VIEW_ITEM_LIST, { category, item_list_name: category, items: [] });
      trackStoreEvent('category_view', { props: { category } });
    },

    // PRAT-83: o id que trackStoreEvent devolve vai como `event_id` para o
    // Pixel (eventID) — é o mesmo que a API de Conversões recebe, e a Meta
    // conta o evento uma vez só.
    search: (term: string, results: number) => {
      const eventId = trackStoreEvent('search', { props: { term, results } });
      analytics?.track?.(EVENTS.SEARCH, { search_term: term, results, event_id: eventId });
    },

    productView: (product: Product) => {
      const eventId = trackStoreEvent('product_view', {
        props: { product_id: product.id, price: product.price, name: product.name },
      });
      analytics?.track?.(EVENTS.VIEW_CONTENT, {
        content_ids: [product.id],
        content_type: 'product',
        value: product.price,
        currency: 'BRL',
        event_id: eventId,
      });
    },

    addToCart: (
      items: Array<{
        id: string;
        quantity: number;
        item_price: number;
      }>,
      totalValue: number,
      coupon?: string,
      discount?: number
    ) => {
      const eventId = trackStoreEvent('add_to_cart', {
        props: {
          product_id: items[0]?.id ?? null,
          quantity: items[0]?.quantity ?? null,
          value: totalValue,
        },
      });
      analytics?.track?.(EVENTS.ADD_TO_CART, {
        content_ids: items.map(item => item.id),
        content_type: 'product',
        contents: items,
        value: totalValue,
        currency: 'BRL',
        coupon,
        discount,
        event_id: eventId,
      });
    },

    checkout: (
      items: Array<{
        id: string;
        quantity: number;
        item_price: number;
      }>,
      totalValue: number,
      coupon?: string,
      discount?: number,
      shipping?: number
    ) => {
      // PRAT-209: card_cta = variação sorteada do apelo do cartão (base do funil por variação).
      const eventId = trackStoreEvent('checkout_started', {
        props: { items: items.length, value: totalValue, card_cta: resolveCardCtaVariant() },
      });
      analytics?.track?.(EVENTS.INITIATE_CHECKOUT, {
        content_ids: items.map(item => item.id),
        content_type: 'product',
        contents: items,
        value: totalValue,
        currency: 'BRL',
        coupon,
        discount,
        shipping,
        event_id: eventId,
      });
    },

    paymentMethod: (
      paymentMethod: string,
      items: Array<{
        id: string;
        quantity: number;
        item_price: number;
      }>,
      totalValue: number,
      coupon?: string,
      discount?: number,
      shipping?: number,
      // PRAT-209: variação do apelo do cartão vista no checkout, para medir.
      extraProps?: Record<string, string | number | boolean | null>
    ) => {
      const eventId = trackStoreEvent('payment_info', {
        props: { ...extraProps, method: paymentMethod, value: totalValue },
      });
      analytics?.track?.(EVENTS.ADD_PAYMENT_INFO, {
        content_ids: items.map(item => item.id),
        content_type: 'product',
        contents: items,
        value: totalValue,
        currency: 'BRL',
        payment_method: paymentMethod,
        coupon,
        discount,
        shipping,
        event_id: eventId,
      });
    },

    purchase: (
      orderId: string,
      items: Array<{
        id: string;
        quantity: number;
        item_price: number;
      }>,
      totalValue: number,
      coupon?: string,
      discount?: number,
      shipping?: number
    ) => {
      analytics?.track?.(EVENTS.PURCHASE, {
        content_ids: items.map(item => item.id),
        content_type: 'product',
        contents: items,
        value: totalValue,
        currency: 'BRL',
        order_id: orderId,
        coupon,
        discount,
        shipping,
        // PRAT-83: o Purchase da API de Conversões sai do servidor com este id.
        event_id: `order-${orderId}`,
      });
    },

    removeFromCart: (productId: string | null, quantity: number) => {
      analytics?.track?.(EVENTS.REMOVE_FROM_CART, { content_ids: [productId], quantity });
      trackStoreEvent('remove_from_cart', { props: { product_id: productId, quantity } });
    },

    checkoutAbandoned: (slug?: string) => {
      trackStoreEvent('checkout_abandoned', { slug, immediate: true });
    },

    signup: () => {
      const eventId = trackStoreEvent('signup', { immediate: true });
      analytics?.track?.(EVENTS.SIGN_UP, { method: 'otp', event_id: eventId });
    },

    // PRAT-83: contato com a loja (hoje, o WhatsApp) — Lead na Meta.
    lead: (channel: string) => {
      const eventId = trackStoreEvent('lead', { props: { channel }, immediate: true });
      analytics?.track?.(EVENTS.LEAD, { content_name: channel, event_id: eventId });
    },

    login: (slug?: string) => {
      analytics?.track?.(EVENTS.LOGIN, { method: 'otp' });
      trackStoreEvent('login', { slug, immediate: true });
    },

    // Eventos de status de pedido
    orderCreated: (orderId: string, totalValue: number) => {
      // O valor que conta no painel é o do pedido gravado na API, não este.
      trackStoreEvent('order_created', { orderId, immediate: true });
      analytics?.track?.(EVENTS.ORDER_CREATED, {
        order_id: orderId,
        value: totalValue,
        currency: 'BRL',
      });
    },

    orderAccepted: (orderId: string, totalValue: number) => {
      analytics?.track?.(EVENTS.ORDER_ACCEPTED, {
        order_id: orderId,
        value: totalValue,
        currency: 'BRL',
      });
    },

    orderOutForDelivery: (orderId: string, totalValue: number) => {
      analytics?.track?.(EVENTS.ORDER_OUT_FOR_DELIVERY, {
        order_id: orderId,
        value: totalValue,
        currency: 'BRL',
      });
    },

    orderReady: (orderId: string, totalValue: number) => {
      analytics?.track?.(EVENTS.ORDER_READY, {
        order_id: orderId,
        value: totalValue,
        currency: 'BRL',
      });
    },

    orderDelivered: (orderId: string, totalValue: number) => {
      analytics?.track?.(EVENTS.ORDER_DELIVERED, {
        order_id: orderId,
        value: totalValue,
        currency: 'BRL',
      });
    },

    orderCanceled: (orderId: string, totalValue: number) => {
      analytics?.track?.(EVENTS.ORDER_CANCELED, {
        order_id: orderId,
        value: totalValue,
        currency: 'BRL',
      });
    },

    // Evento para visualização de lista de produtos
    viewItemList: (category: string, items: Product[]) => {
      analytics?.track?.(EVENTS.VIEW_ITEM_LIST, {
        category,
        items: items.map(item => ({
          item_id: item.id,
          item_name: item.name,
          category,
          price: item.price,
        })),
      });
    },

    // Evento para seleção de item
    selectItem: (product: Product) => {
      analytics?.track?.(EVENTS.SELECT_ITEM, {
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            category: 'product',
            price: product.price,
          },
        ],
      });
    },

    // Evento para informações de envio
    addShippingInfo: (
      shippingType: 'delivery' | 'retirada',
      shippingCost: number,
      items: Array<{
        id: string;
        quantity: number;
        item_price: number;
      }>,
      totalValue: number
    ) => {
      analytics?.track?.(EVENTS.ADD_SHIPPING_INFO, {
        shipping_type: shippingType,
        shipping: shippingCost,
        items: items.map(item => ({
          item_id: item.id,
          item_name: `Product ${item.id}`,
          category: 'product',
          quantity: item.quantity,
          price: item.item_price,
        })),
        value: totalValue,
        currency: 'BRL',
      });
    },

    custom: (eventName: string, data?: Record<string, unknown>) => {
      analytics?.track?.(eventName, data);
    },

    pratigoFooterClick: (source: string) => {
      analytics?.track?.(EVENTS.PRATIGO_FOOTER_CLICK, {
        source,
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
      });
    },

    // Clique no ponto de entrada do Clube PratiGO (vitrine da loja)
    loyaltyEntryClick: (slug: string) => {
      analytics?.track?.(EVENTS.LOYALTY_ENTRY_CLICK, {
        merchant_slug: slug,
        page_url: window.location.href,
      });
    },

    // Visualização do dashboard do Clube PratiGO
    loyaltyDashboardView: (slug: string) => {
      analytics?.track?.(EVENTS.LOYALTY_DASHBOARD_VIEW, {
        merchant_slug: slug,
        page_url: window.location.href,
      });
    },

    // Visualização da tela de detalhes de um produto elegível do Clube PratiGO
    loyaltyProductView: (slug: string, productId: string) => {
      analytics?.track?.(EVENTS.LOYALTY_PRODUCT_VIEW, {
        merchant_slug: slug,
        product_id: productId,
        page_url: window.location.href,
      });
    },

    // Confirmação do resgate de um produto (produto adicionado ao carrinho)
    loyaltyProductRedeem: (slug: string, productId: string, pointsPrice: number) => {
      analytics?.track?.(EVENTS.LOYALTY_PRODUCT_REDEEM, {
        merchant_slug: slug,
        product_id: productId,
        points_price: pointsPrice,
        page_url: window.location.href,
      });
    },

    // Visualização do extrato/histórico de pontos do Clube PratiGO
    loyaltyStatementView: (slug: string) => {
      analytics?.track?.(EVENTS.LOYALTY_STATEMENT_VIEW, {
        merchant_slug: slug,
        page_url: window.location.href,
      });
    },

    // Clique no acesso à indicação (menu/perfil)
    referralEntryClick: () => {
      analytics?.track?.(EVENTS.REFERRAL_ENTRY_CLICK, {
        page_url: window.location.href,
      });
    },

    // Visualização da página de indicação
    referralPageView: () => {
      analytics?.track?.(EVENTS.REFERRAL_PAGE_VIEW, {
        page_url: window.location.href,
      });
    },

    // Compartilhamento da indicação (whatsapp | native | copy)
    // `source` identifica de onde partiu (ex.: 'order_card' no pós-pedido).
    referralShareClick: (method: string, source?: string) => {
      analytics?.track?.(EVENTS.REFERRAL_SHARE_CLICK, {
        method,
        ...(source ? { source } : {}),
        page_url: window.location.href,
      });
    },

    // Cópia do link de indicação
    referralLinkCopy: (source?: string) => {
      analytics?.track?.(EVENTS.REFERRAL_LINK_COPY, {
        ...(source ? { source } : {}),
        page_url: window.location.href,
      });
    },

    // Visualização do card de indicação pós-pedido (página de status/sucesso)
    referralOrderCardView: () => {
      analytics?.track?.(EVENTS.REFERRAL_ORDER_CARD_VIEW, {
        page_url: window.location.href,
      });
    },

    analytics,
  };
}
